/**
 * Reward configuration and badge criteria (R7).
 *
 * Defaults live here in code, not as seeded events (ADR-0002): derived config
 * is DEFAULT_REWARD_CONFIG overridden by the latest reward_config.changed
 * event; the badge set is STARTER_BADGES plus badge.* events (starter badges
 * have fixed well-known IDs so they can be edited/archived by events).
 *
 * PRODUCT VALUES PENDING OWNER APPROVAL: the XP values and level-curve
 * thresholds below are the architect's proposal, escalated in the Wave-0
 * report (XP/curve/badges) and the Wave-0.1 addendum (action XP, date-
 * adjustment bonus, 2026-07-02). Changing a number here is a data edit, not
 * a schema change. Starter badge NAMES are microcopy — ux-designer may
 * revise wording (not structure) before build.
 *
 * Level NAMES/ICONS (`levelIdentities`) are RATIFIED, not pending: PRD OQ11
 * was resolved 2026-07-02 by ux-designer's `design/screens/settings-rewards.md`
 * §1.2 ("keep the ten-level arc, as-is"). The array below is that ratified
 * arc, transcribed verbatim per that spec's explicit instruction to
 * architect/dev-core-domain. Still an editable config field like everything
 * else here (R7.12) — ratified means "the shipped default," not "locked."
 *
 * STARTER_BADGES below reflects Phase 2a ("Rewards v1") scope per
 * `docs/specs/phase-2a-rewards.md` RW-9/D1 (2026-07-02, product-manager):
 * only badges Phase-1-shipped data can actually evaluate ship active;
 * `weekly_reviews`/`checkin_streak_weeks` badges move to
 * `DEFERRED_STARTER_BADGES` until the weekly-review and full check-in
 * screens exist to feed them (Phase 2b+). See that constant's own doc
 * comment for the reactivation contract.
 *
 * Phase 6 (`day.closed`, `priority.completed`, ADR-0002 addendum
 * 2026-07-07): two new XP sources, `dayClosed` and `priorityCompleted`,
 * added below — both PRODUCT VALUES PENDING OWNER APPROVAL, same status as
 * every other number in this file (spec §11 pending-input item 6:
 * standalone-Priority completion XP was left to dev-core-domain/architect to
 * confirm against `XpValues` defaults; resolved here by simply adding the
 * field rather than leaving it XP-neutral — see that field's own doc
 * comment for the reasoning).
 */

import type { BadgeId, HabitId } from './ids';

// ---------------------------------------------------------------------------
// XP values — one entry per XP source, no hidden sources (R7.8 transparency)
// ---------------------------------------------------------------------------

export interface XpValues {
  /** Completing the daily check-in, regardless of habit results (R7.1). */
  readonly checkinCompleted: number;
  /** Each build-habit completion (R7.1). */
  readonly habitCompleted: number;
  /** Each explicit clean day logged on an avoid habit (R7.7). */
  readonly avoidCleanDay: number;
  /**
   * Week finished meeting the plan: build habit reached its target, or avoid
   * habit stayed within its allowance (R7.7 "the weekly bonus"). Applies to
   * both kinds symmetrically — interpretation escalated to owner in Wave-0.
   */
  readonly weeklyTargetBonus: number;
  /** Completing the weekly review (R7.1). */
  readonly reviewCompleted: number;
  /** Realism bonus: accepting a review target adjustment (R7.2). */
  readonly adjustmentAccepted: number;
  /**
   * Each objective action completed (R7 "objective actions and milestones
   * earn XP on completion", v0.9). No separate "milestone completed" value:
   * the v0.9 modeling decision (ADR-0002 addendum) makes a milestone a tag
   * on actions, not a discrete completable event, so there is nothing beyond
   * `actionCompleted` to pay per-action. Whether finishing the LAST action
   * under a milestone tag deserves a bonus on top of this is a product
   * question, not decided here — flagged to the owner in the Wave-0.1
   * report rather than answered silently.
   */
  readonly actionCompleted: number;
  /**
   * Date-realism bonus: accepting an objective date/pace suggestion
   * (R3.4/R4.7, R7 date-adjustment bonus) — the objective-date counterpart of
   * `adjustmentAccepted`. Kept as its OWN field rather than reusing
   * `adjustmentAccepted` even though the default below is set equal to it:
   * habit-target and objective-date recalibration are behaviorally the same
   * "honesty pays" move but are different R7 checkboxes and different event
   * types (`habit.target_adjusted` vs `objective.target_date_adjusted`); a
   * single shared field would silently couple their values under
   * configurability (R7.12 lets the owner edit XP per source) even though
   * the owner may reasonably want to weight them differently later. See
   * Wave-0.1 report for the reasoning; owner may collapse these back to one
   * value with a data edit if preferred — no schema change either way.
   */
  readonly dateAdjustmentAccepted: number;
  /**
   * Closing the day (R8.9, Phase 6, `day.closed`) — a new, date-keyed ritual
   * XP source, mirroring `reviewCompleted`'s "completing a ritual pays a
   * fixed amount" shape but at daily rather than weekly cadence. Idempotent:
   * only the first surviving `day.closed` event per `occurredOn` earns this
   * (ADR-0002 addendum 2026-07-07, mirrors `checkinCompleted`'s dedupe rule).
   */
  readonly dayClosed: number;
  /**
   * Completing a STANDALONE Priority (R8.2, Phase 6, `priority.completed`) —
   * deliberately NOT paid for a linked Priority's completion, which pays
   * through `actionCompleted` instead (the linked Priority's completion IS
   * the action's completion — same event, same XP source, no double-pay;
   * see the ADR addendum's reconciliation section). Resolves spec §11
   * pending-input item 6 ("whether standalone-Priority completion earns its
   * own XP source... not fixed by this spec") by giving it a real,
   * independently-configurable field rather than leaving it XP-neutral —
   * consistent with R7's transparency/configurability requirement either
   * way, and with `actionCompleted`'s own "one checkable thing done" framing
   * (a standalone Priority is exactly that, just without an objective to
   * attribute it to).
   */
  readonly priorityCompleted: number;
}

/**
 * Level curve as explicit cumulative thresholds — fully visible and editable
 * (R7.8/R7.9). Level 1 starts at 0 XP; thresholds[i] is the total lifetime XP
 * required to REACH level i + 2. Must be strictly increasing. Beyond the last
 * entry, each further level requires the last increment again (so the curve
 * never runs out).
 */
export interface LevelCurve {
  readonly thresholds: readonly number[];
}

/**
 * Level identity (PRD §8 "Levels have identity", v0.9): a name and an icon
 * per level, so the daily page can show "<icon> <name> — X XP to <next
 * name>" (R7.4) without exposing the numeric curve there (that stays on the
 * transparency screen, R7.11). Indexed by level number (level 1 = index 0),
 * PARALLEL to `LevelCurve.thresholds` but intentionally a separate array:
 * identity is presentation, the curve is math, and ux-designer must be able
 * to revise names/icons (structure-preserving) without touching XP math, and
 * vice versa for the owner editing thresholds in settings (R7.12).
 *
 * `identities.length` must be `thresholds.length + 1` (one identity for
 * level 1, plus one per threshold reached). Beyond the last defined
 * identity, levels reuse the last entry (mirrors the curve's "reuse the last
 * increment" rule) so an editable curve can never outrun its names.
 *
 * PRD OQ11 RATIFIED — see `design/screens/settings-rewards.md` §1.2
 * ("OQ11 ratification: keep the ten-level arc, as-is", 2026-07-02). Names and
 * icons below are the ratified "journey/navigation" arc, not placeholders.
 * Still editable in settings like any other config field (R7.12) — this is
 * the shipped default, not a lock.
 */
export interface LevelIdentity {
  readonly level: number;
  readonly name: string;
  /** Lucide icon identifier (§8.6: single professional icon set, no emoji). */
  readonly iconId: string;
}

export interface RewardConfig {
  readonly schemaVersion: 1;
  readonly xp: XpValues;
  readonly levelCurve: LevelCurve;
  /** See `LevelIdentity`. Length invariant: `identities.length === levelCurve.thresholds.length + 1`. */
  readonly levelIdentities: readonly LevelIdentity[];
}

// ---------------------------------------------------------------------------
// Badge criteria — the five P0 templates (R7.10) plus one Phase-2a addition
// ---------------------------------------------------------------------------

export type BadgeCriterion =
  | {
      readonly kind: 'habit_completions';
      readonly habitId: HabitId;
      readonly count: number;
    }
  | { readonly kind: 'checkin_streak_weeks'; readonly weeks: number }
  | { readonly kind: 'accepted_recalibrations'; readonly count: number }
  | {
      /**
       * "Clean week" = a finished Mon-Sun week within the habit's allowance
       * (consistent with the weekly bonus; interpretation escalated in Wave-0).
       */
      readonly kind: 'clean_weeks';
      readonly habitId: HabitId;
      readonly weeks: number;
    }
  | { readonly kind: 'weekly_reviews'; readonly count: number }
  | {
      /**
       * Sixth template, added 2026-07-02 for Phase 2a ("Rewards v1",
       * `docs/specs/phase-2a-rewards.md` RW-8) — NOT one of R7.10's original
       * five. Total `action.completed` events across ALL objectives (not
       * scoped to one objective; PRD OQ13 — whether objective-scoped
       * criteria should exist as their own template — is explicitly still
       * open and unresolved by this addition, see that file's §4 escalation
       * and reward-config.ts's own note below `STARTER_BADGES`). Added
       * because it is, along with `accepted_recalibrations`, the only
       * template Phase 1's shipped event log (`action.completed`) can
       * actually evaluate before habits/check-in/weekly-review exist.
       */
      readonly kind: 'action_completions';
      readonly count: number;
    };

export interface BadgeDefinition {
  readonly badgeId: BadgeId;
  readonly name: string;
  /** Icon identifier from the app's single professional icon library. */
  readonly iconId: string;
  readonly criterion: BadgeCriterion;
}

// ---------------------------------------------------------------------------
// Defaults (proposed — owner sign-off pending)
// ---------------------------------------------------------------------------

export const DEFAULT_REWARD_CONFIG: RewardConfig = {
  schemaVersion: 1,
  xp: {
    checkinCompleted: 20,
    habitCompleted: 10,
    avoidCleanDay: 10,
    weeklyTargetBonus: 15,
    reviewCompleted: 40,
    // Deliberately above habitCompleted: recalibrating must feel like the
    // smart play (PRD paragraph 8, reward design).
    adjustmentAccepted: 25,
    // Pegged to habitCompleted: an action is the same granularity of "one
    // checkable thing done" as a habit completion (Wave-0.1 proposal).
    actionCompleted: 10,
    // Set equal to adjustmentAccepted by default (same "honesty pays"
    // weight); kept as an independently configurable field — see XpValues
    // doc comment for why it is not literally the same field.
    dateAdjustmentAccepted: 25,
    // Pegged to reviewCompleted: both are a single, once-per-cadence
    // "closing ritual" (daily vs. weekly) — same register, different
    // frequency (Phase 6, ADR-0002 addendum 2026-07-07).
    dayClosed: 15,
    // Pegged to actionCompleted: a standalone Priority is "one checkable
    // thing done" at the same granularity, just without an objective to
    // attribute it to (Phase 6, ADR-0002 addendum 2026-07-07; see XpValues
    // doc comment).
    priorityCompleted: 10,
  },
  levelCurve: {
    thresholds: [100, 250, 450, 700, 1000, 1400, 1900, 2500, 3200, 4000],
  },
  // Ratified arc (PRD OQ11, settings-rewards.md §1.2, 2026-07-02):
  // "journey/navigation" theme, restrained to one direct Compass pun (level
  // 4, "True North"). One entry per level 1..11 (thresholds.length + 1 =
  // 11): levels 1-10 are the named rungs the spec table lists explicitly;
  // level 11 is NOT an eleventh named rung — per the arc's own "reuse the
  // last identity beyond the defined list" rule (mirrors LevelCurve's
  // "reuse the last increment"), it reuses level 10's name/icon. The spec's
  // own closing caption confirms this: "Levels beyond 10 keep the Master
  // Navigator name/icon with an incrementing numeral (Master Navigator —
  // Level 12, etc.)." Icons are Lucide identifiers (§8.6), chosen to avoid
  // any collision with STARTER_BADGES icons (checked in the spec, §1.2).
  levelIdentities: [
    { level: 1, name: 'First Steps', iconId: 'footprints' },
    { level: 2, name: 'Making Ground', iconId: 'map' },
    { level: 3, name: 'Finding Rhythm', iconId: 'waves' },
    { level: 4, name: 'True North', iconId: 'compass' },
    { level: 5, name: 'Steady Course', iconId: 'anchor' },
    { level: 6, name: 'Open Road', iconId: 'route' },
    { level: 7, name: 'Momentum', iconId: 'wind' },
    { level: 8, name: 'Clear Horizon', iconId: 'telescope' },
    { level: 9, name: 'Way-Finder', iconId: 'mountain-snow' },
    { level: 10, name: 'Master Navigator', iconId: 'star' },
    // Level 11 = "beyond the ratified curve" — reuses level 10's identity
    // verbatim (not a new rung); see comment above.
    { level: 11, name: 'Master Navigator', iconId: 'star' },
  ],
};

/**
 * Starter badges active from first run (R7.10 Phase 1 narrowing).
 *
 * As of 2026-07-02 (phase-2c-weekly-review.md WR-20/AC-W23), every starter
 * template is active: `accepted_recalibrations`/`action_completions` since
 * Phase 1/2a, `checkin_streak_weeks` since Phase 2b, and `weekly_reviews`
 * (the last deferred pair) since this Phase 2c pass. `DEFERRED_STARTER_
 * BADGES` below is now empty — no starter badge concept remains unreachable.
 *
 * `habit_completions` / `clean_weeks` are still absent from both lists (as
 * in the original Phase 1 narrowing) — they take a `habitId` and are
 * structurally uncreatable, not just unearnable, until a habit exists; they
 * remain purely user-created once habits ship, same as before this pass.
 */
export const STARTER_BADGES: readonly BadgeDefinition[] = [
  {
    badgeId: 'starter-first-recalibration' as BadgeId,
    name: 'The Smart Play',
    iconId: 'sliders-horizontal',
    criterion: { kind: 'accepted_recalibrations', count: 1 },
  },
  {
    badgeId: 'starter-three-recalibrations' as BadgeId,
    name: 'Realist',
    iconId: 'target',
    criterion: { kind: 'accepted_recalibrations', count: 3 },
  },
  {
    // RATIFIED 2026-07-02 (dev-core-domain, per orchestrator brief): name
    // and target count finalized as "Off and Running" / 5 actions. `timer`
    // chosen over `footprints` (the obvious first instinct for an
    // early-momentum badge) because `footprints` is already spoken for by
    // `levelIdentities` level 1 ("First Steps") above — the same
    // level-vs-badge icon-collision discipline `settings-rewards.md` §1.2
    // applies to the rest of the set. `timer` is in that spec's curated
    // 31-icon set (§4) and otherwise unused anywhere in this file.
    badgeId: 'starter-first-action' as BadgeId,
    name: 'Off and Running',
    iconId: 'timer',
    criterion: { kind: 'action_completions', count: 5 },
  },
  {
    // RATIFIED 2026-07-02: "In Full Swing" / 25 actions — the higher tier.
    // `zap` deliberately avoids `route` (already used by level 6 "Open
    // Road" above) and is otherwise unused; in the curated set (§4) and
    // reads as sustained energy/momentum, fitting a 25-completion tier.
    badgeId: 'starter-ten-actions' as BadgeId,
    name: 'In Full Swing',
    iconId: 'zap',
    criterion: { kind: 'action_completions', count: 25 },
  },
  {
    // REACTIVATED 2026-07-02 (phase-2b-habits.md §6/AC-B22): the full
    // check-in flow now produces `checkin.completed`, so
    // `RewardSnapshot.streakDays` is real and this criterion has a genuine
    // signal — moved back from `DEFERRED_STARTER_BADGES` verbatim (same
    // badgeId, name, icon; only the array it lives in changed), exactly the
    // "one-line move" that constant's own doc comment anticipated.
    badgeId: 'starter-one-week-streak' as BadgeId,
    name: 'One Full Week',
    iconId: 'flame',
    criterion: { kind: 'checkin_streak_weeks', weeks: 1 },
  },
  {
    // REACTIVATED 2026-07-02 — see note above.
    badgeId: 'starter-four-week-streak' as BadgeId,
    name: 'Four Weeks Showing Up',
    iconId: 'mountain',
    criterion: { kind: 'checkin_streak_weeks', weeks: 4 },
  },
  {
    // REACTIVATED 2026-07-02 (phase-2c-weekly-review.md WR-20/AC-W23): the
    // weekly review screen now produces `review.completed`, so this
    // criterion has a genuine signal for the first time — moved back from
    // `DEFERRED_STARTER_BADGES` verbatim (same badgeId, name, icon; only the
    // array it lives in changed), the exact "one-line move" that constant's
    // own doc comment (below) anticipated.
    badgeId: 'starter-first-review' as BadgeId,
    name: 'First Reflection',
    iconId: 'sunrise',
    criterion: { kind: 'weekly_reviews', count: 1 },
  },
  {
    // REACTIVATED 2026-07-02 — see note above.
    badgeId: 'starter-four-reviews' as BadgeId,
    name: 'A Month of Sundays',
    iconId: 'calendar-check',
    criterion: { kind: 'weekly_reviews', count: 4 },
  },
];

/**
 * Starter badges withheld from the active set because the shipped event log
 * could not yet produce data for their criteria. Now EMPTY (2026-07-02,
 * phase-2c-weekly-review.md WR-20/AC-W23): the `checkin_streak_weeks` pair
 * reactivated in Phase 2b, and the `weekly_reviews` pair — the last deferred
 * pair, per this constant's own prior comment — reactivates in this Phase 2c
 * pass now that the weekly review screen produces `review.completed`. No
 * starter badge concept remains unreachable. Kept as an exported (empty)
 * constant rather than deleted — `assembleLiveBadges`/tests reference it
 * directly (AC-R13/AC-W23 assert against it), and a future starter template
 * that genuinely needs staging before its producing event ships would reuse
 * this same deferral mechanism.
 */
export const DEFERRED_STARTER_BADGES: readonly BadgeDefinition[] = [];
